import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { paymentGateway } from "../../../api/bseApi";

const PAYMENT_MODES = [
  { value: "NB", label: "Net Banking" },
  { value: "UPI", label: "UPI" },
  { value: "NEFT", label: "NEFT / RTGS" },
];

/** Common Indian banks for Net Banking - BankCode values per BSE */
const BANKS = [
  { code: "HDFC0001", name: "HDFC Bank" },
  { code: "ICIC0001", name: "ICICI Bank" },
  { code: "SBIN0001", name: "State Bank of India" },
  { code: "AXIS0001", name: "Axis Bank" },
  { code: "UTIB0001", name: "Kotak Mahindra Bank" },
  { code: "YESB0001", name: "Yes Bank" },
  { code: "INDB0001", name: "IndusInd Bank" },
  { code: "FDRL0001", name: "Federal Bank" },
  { code: "PNB00001", name: "Punjab National Bank" },
  { code: "BARB0001", name: "Bank of Baroda" },
  { code: "IDFB0001", name: "IDFC First Bank" },
  { code: "CNRB0001", name: "Canara Bank" },
  { code: "UNCB0001", name: "Union Bank of India" },
  { code: "PUNB0001", name: "Punjab & Sind Bank" },
  { code: "BOML0001", name: "Bank of Maharashtra" },
];

const NEFT_INSTRUCTIONS = `
For NEFT/RTGS payment:

1. Log in to your bank's net banking or mobile app
2. Add Beneficiary: BSE StAR MF (as per BSE registration)
3. Transfer the exact amount to the registered account
4. Use the Order No as reference in the transfer
5. Payment will be credited within 1-2 business days

After completing the transfer, you can check payment status from Order Status / Portfolio screen.
`;

/**
 * PaymentModeSelectionScreen - BSE StAR MF Payment Gateway (MFAPI Flag 03)
 * Opens immediately after successful order placement
 * Receives: OrderNo, ClientCode, Payable Amount
 */
export default function PaymentModeSelectionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({
    paymentMode: "",
    bankCode: "",
    upiId: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const orderNo = state?.orderNo || params.get("orderNo");
    const clientCode = state?.clientCode || params.get("clientCode");
    const amount = state?.amount ?? params.get("amount");

    if (!orderNo || !clientCode || amount == null || amount === "") {
      Swal.fire("Error", "Missing order details. Open from order placement flow.", "error").then(() => navigate("/user"));
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      Swal.fire("Error", "Invalid amount.", "error").then(() => navigate("/user"));
      return;
    }

    setContext({
      orderNo: String(orderNo).trim(),
      clientCode: String(clientCode).trim(),
      amount: numAmount,
    });
  }, [location.state, location.search, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!form.paymentMode) err.paymentMode = "Payment mode must be selected";
    if (form.paymentMode === "NB") {
      if (!form.bankCode?.trim()) err.bankCode = "Bank selection is mandatory for Net Banking";
    }
    if (form.paymentMode === "UPI") {
      const upi = (form.upiId || "").trim();
      if (!upi) err.upiId = "UPI ID is mandatory for UPI";
      else if (!/^[\w.-]+@[\w.-]+$/.test(upi) && !/^\d{10}$/.test(upi)) {
        err.upiId = "Enter valid UPI ID (e.g. name@upi) or mobile number";
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/payment/return`;
      const payload = {
        orderNo: context.orderNo,
        clientCode: context.clientCode,
        amount: context.amount,
        paymentMode: form.paymentMode,
        returnUrl,
      };
      if (form.paymentMode === "NB") payload.bankCode = form.bankCode.trim();
      if (form.paymentMode === "UPI") payload.upiId = form.upiId.trim();

      const res = await paymentGateway(payload);

      if (res.data?.status === 1) {
        const { paymentUrl, isNeft } = res.data.data || {};
        const bseFailed = res.data?.bseFailed === true;

        if (isNeft || form.paymentMode === "NEFT") {
          const extraMsg = bseFailed ? `<p class="text-warning mb-0">${res.data?.message || "BSE gateway was not reached."}</p>` : "";
          Swal.fire({
            icon: "info",
            title: "NEFT / RTGS Selected",
            html: `${extraMsg}<p class="text-start mt-2">${NEFT_INSTRUCTIONS.replace(/\n/g, "<br>")}</p><p class="mt-2"><strong>Order No:</strong> ${context.orderNo}<br><strong>Amount:</strong> ₹${context.amount.toLocaleString("en-IN")}</p>`,
            confirmButtonText: "Done",
          }).then(() => navigate("/user"));
          return;
        }

        if (paymentUrl) {
          window.location.href = paymentUrl;
          return;
        }

        if (bseFailed) {
          Swal.fire({
            icon: "warning",
            title: "Payment recorded",
            text: res.data?.message || "BSE gateway could not be reached. You may use NEFT/RTGS or retry later.",
          }).then(() => navigate("/user"));
          return;
        }

        Swal.fire("Success", "Payment gateway initiated.", "success").then(() => navigate("/user"));
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Payment gateway failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        err.message ||
        "Payment gateway failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Payment Mode</li>
              </ol>
            </nav>
            <h5>Payment Mode Selection</h5>
            <p className="text-muted mb-0">
              Order No: <strong>{context.orderNo}</strong> | Client: <strong>{context.clientCode}</strong> | Amount: <strong>₹{context.amount.toLocaleString("en-IN")}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Payment Mode Selection
              </h6>

              <div className="mb-4">
                <label className="form-label">Select Payment Mode <span className="text-danger">*</span></label>
                <div className="d-flex flex-wrap gap-3">
                  {PAYMENT_MODES.map((m) => (
                    <div key={m.value} className="form-check">
                      <input
                        type="radio"
                        className={`form-check-input ${errors.paymentMode ? "is-invalid" : ""}`}
                        name="paymentMode"
                        id={`pm-${m.value}`}
                        value={m.value}
                        checked={form.paymentMode === m.value}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor={`pm-${m.value}`}>
                        {m.label}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.paymentMode && <div className="invalid-feedback d-block">{errors.paymentMode}</div>}
              </div>

              {form.paymentMode === "NB" && (
                <div className="mb-4">
                  <label className="form-label">Bank (BankCode) <span className="text-danger">*</span></label>
                  <select
                    className={`form-select ${errors.bankCode ? "is-invalid" : ""}`}
                    name="bankCode"
                    value={form.bankCode}
                    onChange={handleChange}
                  >
                    <option value="">Select Bank</option>
                    {BANKS.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.name} ({b.code})
                      </option>
                    ))}
                  </select>
                  {errors.bankCode && <div className="invalid-feedback">{errors.bankCode}</div>}
                </div>
              )}

              {form.paymentMode === "UPI" && (
                <div className="mb-4">
                  <label className="form-label">UPI ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.upiId ? "is-invalid" : ""}`}
                    name="upiId"
                    value={form.upiId}
                    onChange={handleChange}
                    placeholder="e.g. name@upi or 9876543210"
                  />
                  {errors.upiId && <div className="invalid-feedback">{errors.upiId}</div>}
                </div>
              )}

              {form.paymentMode === "NEFT" && (
                <div className="alert alert-info mb-4">
                  <strong>NEFT / RTGS:</strong> You will receive bank transfer instructions. No redirect to payment gateway.
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Skip / Later
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Processing...
                    </>
                  ) : (
                    "Proceed to Payment"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
